import {
  BenefitsContract,
  CategorizedBenefits,
  Contract,
  Eligibilities,
  LSCommon,
  Product,
} from '@legalshield/legalshield-member-react-common';
import { BenefitPage } from '@legalshield/products-ux-framework';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { getHeaderImage, getSupplementTitle, useQuery, interpolate, contractUrlWithId } from '../../utils';
import { useLanguage } from '../../hooks/useLanguage';
import ErrorCard from '../ErrorCard';
import Loading from '../Loading';
import '@legalshield/products-ux-framework/dist/index.css';
import { ContractInfo } from '@legalshield/products-ux-framework/dist/components/BenefitPage/BenefitPage.types';

type QueryContract = {
  planContractId: string;
  planOverviewId?: string;
  supplementContractsId: string[];
};

// Extended interface for BenefitsContract that includes statesVariants
interface ExtendedBenefitsContract extends BenefitsContract {
  statesVariants?: Array<{
    state: string;
    fullFileName: string;
    summaryFileName?: string;
  }>;
}

const ProductBenefit = (): React.ReactElement => {
  const query = useQuery();
  const stateVariantQueryParam = query.get('stateVariant');
  const language = useLanguage();

  const parseSupplements = (supplements: string): { [supplement: string]: boolean } => {
    const result = {};
    supplements.split(',').forEach((supplement) => (result[supplement] = true));
    return result;
  };

  const parseContracts = (contractIds?: string, planOverviewId?: string | null): QueryContract | null => {
    if (contractIds) {
      const ids = contractIds.split(',');

      return {
        planContractId: ids[0],
        planOverviewId: planOverviewId,
        supplementContractsId: ids.slice(1, ids.length),
      };
    }

    return null;
  };

  const product: Product = useMemo(
    () =>
      ({
        id: query.get('id'),
        productFamily: query.get('productFamily'),
        productType: query.get('productType'),
        productVersion: query.get('productVersion'),
        supplements: query.get('supplements') && parseSupplements(query.get('supplements')),
        tier: query.get('tier'),
      } as Product),
    [query],
  );
  // Contracts parsed from contractIds and productOverview query parameters
  const contractsFromQuery = useMemo(
    () => parseContracts(query.get('contractsIds'), query.get('productOverview')),
    [query],
  );
  const [eligibilities, setEligibilities] = useState<Eligibilities | undefined>(undefined);
  const [categories, setCategories] = useState<CategorizedBenefits[] | undefined>(undefined);
  const [error, setError] = useState<AxiosError | undefined>(undefined);
  const title = `${eligibilities?.productBenefits?.friendlyName ?? string_table.GENERIC_BENEFIT_FRIENDLY_NAME}${
    product.tier ? ` - ${product.tier}` : ''
  }`;

  useEffect(() => {
    LSCommon.getInstance().benefits().fetchProductBenefits(product).then(setEligibilities).catch(setError);
  }, [product]);

  if (error) {
    <ErrorCard message={error.message} />;
  }

  useEffect(() => {
    if (eligibilities) {
      setCategories(LSCommon.getInstance().benefits().categorizeBenefits(eligibilities));
    }
  }, [eligibilities]);

  const getProductContracts = (): ContractInfo[] => {
    const productContract = eligibilities?.productBenefits?.contract as ExtendedBenefitsContract;
    const productFriendlyName = eligibilities?.productBenefits?.friendlyName
      ? interpolate(string_table.BENEFITS_PAGE_VIEW_CONTRACT_TITLE, eligibilities?.productBenefits?.friendlyName)
      : string_table.GENERIC_BENEFIT_FRIENDLY_NAME;

    // Check for state variations matches based on the query parameter and return those contracts if they exist
    if (stateVariantQueryParam && productContract?.statesVariants) {
      const stateVariantFound = productContract.statesVariants.find(
        (variant) => variant.state === stateVariantQueryParam,
      );
      if (stateVariantFound) {
        const returnContracts = [];
        if (stateVariantFound.fullFileName) {
          returnContracts.push({
            name: productFriendlyName,
            url: contractUrlWithId(stateVariantFound.fullFileName),
          });
        }
        if (stateVariantFound.summaryFileName) {
          returnContracts.push({
            name: string_table.BENEFITS_PAGE_PLAN_OVERVIEW_LINK_TITLE,
            url: contractUrlWithId(stateVariantFound.summaryFileName),
          });
          return returnContracts;
        }
      }
    }

    if (contractsFromQuery) {
      const queryContractInfos: ContractInfo[] = [];
      if (contractsFromQuery.planContractId) {
        queryContractInfos.push({
          name: productFriendlyName,
          url: contractUrlWithId(contractsFromQuery.planContractId) || '',
        });
      }
      if (contractsFromQuery.planOverviewId) {
        queryContractInfos.push({
          name: string_table.BENEFITS_PAGE_PLAN_OVERVIEW_LINK_TITLE,
          url: contractUrlWithId(contractsFromQuery.planOverviewId) || '',
        });
      }
      return queryContractInfos;
    }
    const productContractUrls = [];
    if (productContract?.fullUri) {
      productContractUrls.push({
        name: productFriendlyName,
        url: productContract.fullUri,
      });
    }
    if (productContract?.summaryUri) {
      productContractUrls.push({
        name: string_table.BENEFITS_PAGE_PLAN_OVERVIEW_LINK_TITLE,
        url: productContract.summaryUri,
      });
    }
    return productContractUrls;
  };

  const getSupplementContract = (): ContractInfo[] => {
    const entitlementSupplementContracts = contractsFromQuery?.supplementContractsId?.map((id: string) => ({
      contractDate: '0',
      id: id,
      isSupplement: true,
    }));
    const supplementContracts = eligibilities?.supplementBenefits?.map((benefits) => benefits.contract).filter(Boolean);

    if (contractsFromQuery) {
      return entitlementSupplementContracts?.map(
        (contract: Contract, index: number): ContractInfo => ({
          name:
            eligibilities?.supplementBenefits.length === 1
              ? interpolate(
                  string_table.BENEFITS_PAGE_VIEW_CONTRACT_TITLE,
                  eligibilities.supplementBenefits[0].friendlyName,
                )
              : interpolate(string_table.BENEFITS_PAGE_SUPPLEMENT_LINK_TITLE, (index + 1).toString()),
          url: contractUrlWithId(contract.id) || '',
        }),
      );
    }

    return supplementContracts?.map(
      (contract: BenefitsContract, index: number): ContractInfo => ({
        name:
          eligibilities?.supplementBenefits.length === 1
            ? interpolate(
                string_table.BENEFITS_PAGE_VIEW_CONTRACT_TITLE,
                eligibilities.supplementBenefits[0].friendlyName,
              )
            : interpolate(string_table.BENEFITS_PAGE_SUPPLEMENT_LINK_TITLE, `${index + 1}`),
        url: contract.fullUri,
      }),
    );
  };

  const getContracts = (): ContractInfo[] => {
    const productContracts = getProductContracts();
    const supplementContracts = getSupplementContract();
    return [...productContracts, ...supplementContracts];
  };

  const onContractPressed = (contractInfo: ContractInfo): void => {
    if (contractInfo.url) {
      window.open(contractInfo.url);
    }
  };

  if (eligibilities) {
    return (
      <BenefitPage
        categories={categories}
        title={title}
        supplementTitle={getSupplementTitle(eligibilities)}
        contracts={getContracts()}
        onPressContract={onContractPressed}
        language={language}
      />
    );
  }

  return <Loading />;
};

export default ProductBenefit;
